import React, { useLayoutEffect, useRef } from "react";

import { color } from "emoreg/const";
import { Box } from "emoreg/layouts";

import {
  isDropdownPopOverContentComp,
  isDropdownPopOverFooterComp,
  isDropdownPopOverHeaderComp,
} from "./utils";

type DropdownOverChildProps = {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export type DropdownPopOverProps = Partial<DropdownOverChildProps> & {
  onFocus?: () => void;
  onBlur?: () => void;
  setFloating?: (node: HTMLElement | null) => void;
  style?: React.CSSProperties | ((anchorElem: HTMLElement) => Partial<CSSStyleDeclaration>);
  children:
    | JSX.Element
    | JSX.Element[]
    | ((props: DropdownOverChildProps) => JSX.Element | JSX.Element[]);
};

export const DropdownPopOver = ({
  children,
  onFocus,
  onBlur,
  style,
  setIsOpen,
  setFloating,
}: DropdownPopOverProps) => {
  if (!onFocus || !onBlur || !style || !setIsOpen || !setFloating) {
    throw Error("DropdownContent should be used as direct child of Dropdown");
  }

  const contentRef = useRef<HTMLDivElement | null>();

  useLayoutEffect(() => {
    contentRef.current?.focus();
  }, []);

  const onRef = (ref: HTMLDivElement | null) => {
    setFloating(ref);
    contentRef.current = ref;
  };

  // filters children to order them and keep only allowed children
  const child = typeof children === "function" ? children({ setIsOpen }) : children;
  const childList = React.Children.toArray(child);
  const header = childList.find(isDropdownPopOverHeaderComp);
  const content = childList.find(isDropdownPopOverContentComp);
  const footer = childList.find(isDropdownPopOverFooterComp);

  // ensure that a content is provided
  if (!content) throw Error("Dropdown.PopOver must contain a Dropdown.PopOver.Content");

  return (
    <Box
      tabIndex={-1}
      ref={onRef}
      onFocus={onFocus}
      onBlur={onBlur}
      style={typeof style === "function" ? {} : style}
      radius={6}
      boxShadow="2px 4px 16px 0px #00000030"
      backgroundColor={color.white}
    >
      {header}
      {content}
      {footer}
    </Box>
  );
};
